import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDate, formatDateTime, formatTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormPrice,
  FormRoot,
  RHF,
  Separator,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useParams } from "@tanstack/react-router";
import {
  Building2Icon,
  CalendarClockIcon,
  FileTextIcon,
  Loader2Icon,
  PackageIcon,
  PrinterIcon,
  UserIcon,
  WalletIcon,
  BanIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  useApplyCreditPaymentMutation,
  useCreditInvoiceQuery,
  useReconcileCreditPaymentMutation,
} from "../api/queries";
import type { CreditPaymentRowDTO } from "../api/client";
import { formatCreditInvoiceStatusLa } from "../lib/credit-invoice-status";
import { printPaidCreditInvoice } from "../lib/print-paid-credit-invoice";
import { VoidCreditInvoiceDialog } from "../ui/VoidCreditInvoiceDialog";
import { VoidCreditPaymentDialog } from "../ui/VoidCreditPaymentDialog";

const PayFormSchema = z.object({
  amount: z.coerce.number().int().positive("ຕ້ອງຫຼາຍກວ່າ 0"),
  notes: z.string().optional(),
});

type PayFormValues = z.infer<typeof PayFormSchema>;

function refTypeLabel(refType: string): string {
  switch (refType) {
    case "room_booking":
      return "ຈອງຫ້ອງ";
    case "package_purchase":
      return "ແພັກເກດຄລາດສ໌";
    case "private_lesson_package":
      return "ແພັກເກດຮຽນສ່ວນຕົວ";
    case "partner_project_sale":
      return "Partner ອອກສະຖານທີ່";
    default:
      return refType;
  }
}

function DetailBlock({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="font-medium text-[0.65rem] text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <div className="text-sm leading-snug">{children}</div>
    </div>
  );
}

export function CreditInvoiceDetailPage() {
  const { id } = useParams({ from: "/app/billing/credit-invoices/$id" });
  const { data: invoice, isLoading } = useCreditInvoiceQuery(id ?? null);
  const payMutation = useApplyCreditPaymentMutation(id ?? "");
  const reconcileMutation = useReconcileCreditPaymentMutation(id ?? "");
  const canVoid = useActionPermission("billing:void");
  const [voidPayment, setVoidPayment] = useState<CreditPaymentRowDTO | null>(
    null,
  );
  const [voidInvoiceOpen, setVoidInvoiceOpen] = useState(false);

  const methods = RHF.useForm<PayFormValues>({
    // @ts-expect-error zodResolver + z.coerce typing (same pattern as SellPackageForm)
    resolver: zodResolver(PayFormSchema),
    defaultValues: { amount: 0, notes: "" },
  });

  useEffect(() => {
    if (invoice && invoice.remainingAmount > 0) {
      methods.setValue("amount", invoice.remainingAmount);
    }
  }, [invoice, methods]);

  const onPay = async (vals: PayFormValues) => {
    if (!id) return;
    try {
      await payMutation.mutateAsync({
        amount: vals.amount,
        notes: vals.notes?.trim() || undefined,
      });
      toast.success("ບັນທຶກການຮັບເງິນແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດບັນທຶກໄດ້");
    }
  };

  const onReconcile = async (paymentId: string) => {
    try {
      await reconcileMutation.mutateAsync(paymentId);
      toast.success("ຜູກຜູ້ຮັບເງິນແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດຜູກໄດ້");
    }
  };

  const paidPercent =
    invoice && invoice.totalAmount > 0
      ? Math.min(
        100,
        Math.round((invoice.paidAmount / invoice.totalAmount) * 100),
      )
      : 0;

  const contextTitle =
    invoice?.refType === "room_booking"
      ? "ຂໍ້ມູນການຈອງ"
      : invoice?.refType === "partner_project_sale"
        ? "ຂໍ້ມູນ Partner"
      : invoice?.refType === "package_purchase" ||
        invoice?.refType === "private_lesson_package"
        ? "ຂໍ້ມູນແພັກເກດ"
        : "ຂໍ້ມູນອ້າງອີງ";

  const ContextIcon =
    invoice?.refType === "room_booking"
      ? Building2Icon
      : invoice?.refType === "package_purchase" ||
        invoice?.refType === "private_lesson_package"
        ? PackageIcon
        : FileTextIcon;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            {
              label: "ໃບເກັບເງິນ",
              to: "/app/billing/credit-invoices",
            },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        {isLoading && !invoice ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <Loader2Icon
              className="size-8 animate-spin text-muted-foreground"
              aria-hidden
            />
          </div>
        ) : invoice ? (
          <div className="mx-auto max-w-6xl">
            <header className="mb-8 border-border border-b pb-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <FileTextIcon
                        className="size-7 shrink-0 stroke-[1.5] text-muted-foreground"
                        aria-hidden
                      />
                      <h1 className="font-bold text-2xl tracking-tight">
                        ລາຍລະອຽດໃບເກັບເງິນ
                      </h1>
                    </div>
                    <Badge variant="outline" className="font-normal">
                      {refTypeLabel(invoice.refType)}
                    </Badge>
                    <Badge variant="secondary" className="font-normal">
                      {formatCreditInvoiceStatusLa(invoice.status)}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarClockIcon
                        className="size-3.5 shrink-0 stroke-[1.5]"
                        aria-hidden
                      />
                      ອອກເມື່ອ {formatDateTime(invoice.issuedAt)}
                    </span>
                    <span className="mx-2 text-border">·</span>
                    <span className="font-mono text-xs">{invoice.id}</span>
                  </p>
                  {invoice.status === "cancelled" && invoice.voidedAt ? (
                    <p className="text-destructive text-sm">
                      ຍົກເລີກໃບເມື່ອ {formatDateTime(invoice.voidedAt)}
                      {invoice.voidReason
                        ? ` — ${invoice.voidReason}`
                        : null}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2 sm:pt-1">
                  {invoice.status === "paid" ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void printPaidCreditInvoice(invoice)}
                    >
                      <PrinterIcon className="mr-2 size-4" aria-hidden />
                      ພິມໃບ
                    </Button>
                  ) : null}
                  {canVoid &&
                  invoice.status !== "cancelled" &&
                  invoice.refType !== "partner_project_sale" ? (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setVoidInvoiceOpen(true)}
                    >
                      <BanIcon className="mr-2 size-4" aria-hidden />
                      ຍົກເລີກໃບ
                    </Button>
                  ) : null}
                </div>
              </div>
            </header>

            <div
              className={
                invoice.remainingAmount > 0
                  ? "grid gap-6 lg:grid-cols-12"
                  : "grid gap-6"
              }
            >
              <div
                className={
                  invoice.remainingAmount > 0
                    ? "space-y-6 lg:col-span-8"
                    : "space-y-6"
                }
              >
                <Card className="gap-0 overflow-hidden p-0">
                  <CardHeader className="border-border border-b bg-muted/30 py-4">
                    <CardTitle className="flex items-center gap-2 font-medium text-base">
                      <ContextIcon
                        className="size-4 shrink-0 stroke-[1.5] text-muted-foreground"
                        aria-hidden
                      />
                      {contextTitle}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-4">
                    <dl className="grid gap-6 text-sm sm:grid-cols-2">
                      {invoice.refType === "room_booking" ? (
                        <>
                          <DetailBlock label="ຜູ້ຕິດຕໍ່">
                            <p className="font-medium">
                              {invoice.contactName ?? "—"}
                              {invoice.contactPhone
                                ? ` · ${invoice.contactPhone}`
                                : ""}
                            </p>
                          </DetailBlock>
                          <DetailBlock label="ຫ້ອງ">
                            <p className="font-medium">
                              {invoice.roomName ?? "—"}
                            </p>
                          </DetailBlock>
                          <DetailBlock label="ວັນທີ / ເວລາ">
                            <p className="font-medium">
                              {invoice.bookingDate
                                ? formatDate(invoice.bookingDate)
                                : "—"}
                              {invoice.bookingStartTime &&
                                invoice.bookingEndTime
                                ? ` · ${formatTime(invoice.bookingStartTime)} – ${formatTime(invoice.bookingEndTime)}`
                                : ""}
                            </p>
                          </DetailBlock>
                        </>
                      ) : invoice.refType === "partner_project_sale" ? (
                        <DetailBlock label="ໂຄງການ">
                          <p className="font-medium">
                            {invoice.packageContextLabel ?? "—"}
                          </p>
                        </DetailBlock>
                      ) : (
                        <>
                          <DetailBlock label="ນັກຮຽນ">
                            <p className="flex items-start gap-2 font-medium">
                              <UserIcon
                                className="mt-0.5 size-4 shrink-0 stroke-[1.5] text-muted-foreground"
                                aria-hidden
                              />
                              <span>
                                {invoice.studentName ?? "—"}
                                {invoice.studentMemberId
                                  ? ` · ${invoice.studentMemberId}`
                                  : ""}
                              </span>
                            </p>
                          </DetailBlock>
                          <DetailBlock label="ແພັກເກດ">
                            <p className="font-medium">
                              {invoice.packageContextLabel ?? "—"}
                            </p>
                          </DetailBlock>
                        </>
                      )}
                    </dl>
                    {invoice.notes ? (
                      <>
                        <Separator className="my-6" />
                        <DetailBlock label="ໝາຍເຫດ">
                          <p className="whitespace-pre-wrap text-muted-foreground">
                            {invoice.notes}
                          </p>
                        </DetailBlock>
                      </>
                    ) : null}
                  </CardContent>
                </Card>

                <Card className="gap-0 overflow-hidden border-l-4 border-l-red-600">
                  <CardHeader className="border-border border-b bg-muted/30 pb-4">
                    <CardTitle className="flex items-center gap-2 font-medium text-base">
                      <WalletIcon
                        className="size-4 shrink-0 stroke-[1.5] text-muted-foreground"
                        aria-hidden
                      />
                      ຍອດເງິນ
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-6">
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="rounded-lg border bg-background p-4">
                        <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ລວມ
                        </p>
                        <p className="mt-2 font-semibold text-xl tabular-nums tracking-tight">
                          {invoice.totalAmount.toLocaleString("lo-LA")}
                          <span className="ml-1 font-normal text-muted-foreground text-sm">
                            ກີບ
                          </span>
                        </p>
                      </div>
                      <div className="rounded-lg border bg-background p-4">
                        <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ຈ່າຍແລ້ວ
                        </p>
                        <p className="mt-2 font-semibold text-xl tabular-nums tracking-tight">
                          {invoice.paidAmount.toLocaleString("lo-LA")}
                          <span className="ml-1 font-normal text-muted-foreground text-sm">
                            ກີບ
                          </span>
                        </p>
                      </div>
                      <div
                        className={
                          invoice.remainingAmount > 0
                            ? "rounded-lg border border-red-600/40 bg-background p-4"
                            : "rounded-lg border bg-background p-4"
                        }
                      >
                        <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ຄ້າງ
                        </p>
                        <p
                          className={
                            invoice.remainingAmount > 0
                              ? "mt-2 font-semibold text-red-600 text-xl tabular-nums tracking-tight"
                              : "mt-2 font-semibold text-xl tabular-nums tracking-tight"
                          }
                        >
                          {invoice.remainingAmount.toLocaleString("lo-LA")}
                          <span className="ml-1 font-normal text-muted-foreground text-sm">
                            ກີບ
                          </span>
                        </p>
                      </div>
                    </div>
                    {invoice.totalAmount > 0 ? (
                      <div>
                        <div className="mb-2 flex justify-between text-muted-foreground text-xs">
                          <span>ຄວາມຄືບໜ້າການຊຳລະ</span>
                          <span className="tabular-nums">{paidPercent}%</span>
                        </div>
                        <div
                          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                          role="progressbar"
                          aria-valuenow={paidPercent}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label="ຄວາມຄືບໜ້າການຊຳລະ"
                        >
                          <div
                            className="h-full rounded-full bg-red-600 transition-[width]"
                            style={{ width: `${paidPercent}%` }}
                          />
                        </div>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>

                {(invoice.installments ?? []).length > 0 ? (
                  <Card className="gap-0 overflow-hidden">
                    <CardHeader className="border-border border-b bg-muted/30 pb-4">
                      <CardTitle className="font-medium text-base">
                        ແຜນງວດຊຳລະ
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/20 text-left text-muted-foreground text-xs">
                              <th className="px-4 py-2 font-medium">ງວດ</th>
                              <th className="px-4 py-2 font-medium">ຄົບກຳນົດ</th>
                              <th className="px-4 py-2 font-medium text-right">
                                ຍອດ
                              </th>
                              <th className="px-4 py-2 font-medium text-right">
                                ຈ່າຍແລ້ວ
                              </th>
                              <th className="px-4 py-2 font-medium">ສະຖານະ</th>
                              <th className="px-4 py-2 font-medium" />
                            </tr>
                          </thead>
                          <tbody>
                            {(invoice.installments ?? []).map((inst) => {
                              const remain = inst.amount - inst.paidAmount;
                              const canPay =
                                invoice.remainingAmount > 0 &&
                                remain > 0 &&
                                invoice.status !== "cancelled";
                              return (
                                <tr
                                  key={inst.id}
                                  className="border-b last:border-b-0"
                                >
                                  <td className="px-4 py-3 font-medium">
                                    {inst.label ?? `ງວດ ${inst.installmentNo}`}
                                  </td>
                                  <td className="px-4 py-3 text-muted-foreground">
                                    {inst.dueDate
                                      ? formatDate(inst.dueDate)
                                      : "—"}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums">
                                    {inst.amount.toLocaleString("lo-LA")}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums">
                                    {inst.paidAmount.toLocaleString("lo-LA")}
                                  </td>
                                  <td className="px-4 py-3">
                                    <Badge variant="outline" className="font-normal">
                                      {inst.status === "paid"
                                        ? "ຊຳລະແລ້ວ"
                                        : inst.status === "partial"
                                          ? "ບາງສ່ວນ"
                                          : "ຄ້າງ"}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {canPay ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          methods.setValue(
                                            "amount",
                                            Math.min(
                                              remain,
                                              invoice.remainingAmount,
                                            ),
                                          );
                                        }}
                                      >
                                        ຮັບງວດນີ້
                                      </Button>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ) : null}

                <Card className="gap-0 overflow-hidden">
                  <CardHeader className="border-border border-b bg-muted/30 pb-4">
                    <CardTitle className="font-medium text-base">
                      ປະຫວັດການຮັບເງິນ
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {invoice.payments.length === 0 ? (
                      <p className="py-10 text-center text-muted-foreground text-sm">
                        ຍັງບໍ່ມີການຮັບເງິນ
                      </p>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="border-b bg-muted/40 text-left">
                              <th className="px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                ວັນທີ
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                ຈຳນວນ
                              </th>
                              <th className="px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                ຜູ້ຮັບ (ລະບົບ)
                              </th>
                              <th className="px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                ໝາຍເຫດ
                              </th>
                              <th className="px-4 py-3" />
                            </tr>
                          </thead>
                          <tbody>
                            {invoice.payments.map((p) => {
                              const isVoided = p.status === "voided";
                              return (
                              <tr
                                key={p.id}
                                className={
                                  isVoided
                                    ? "border-border/80 border-b bg-muted/20 text-muted-foreground last:border-b-0"
                                    : "border-border/80 border-b last:border-b-0"
                                }
                              >
                                <td className="px-4 py-3">
                                  {formatDateTime(p.paidAt)}
                                  {isVoided && p.voidedAt ? (
                                    <p className="mt-0.5 text-xs">
                                      ຍົກເລີກ {formatDateTime(p.voidedAt)}
                                    </p>
                                  ) : null}
                                </td>
                                <td className="px-4 py-3 text-right font-medium tabular-nums">
                                  <span
                                    className={
                                      isVoided ? "line-through opacity-70" : ""
                                    }
                                  >
                                    {p.amount.toLocaleString("lo-LA")} ກີບ
                                  </span>
                                </td>
                                <td className="max-w-48 truncate px-4 py-3">
                                  {p.paidBy == null ? (
                                    <span className="text-muted-foreground">
                                      Studio / ຍັງບໍ່ຜູກ
                                    </span>
                                  ) : (
                                    (p.paidByName ?? p.paidBy)
                                  )}
                                </td>
                                <td className="max-w-56 truncate px-4 py-3 text-muted-foreground">
                                  {isVoided && p.voidReason
                                    ? p.voidReason
                                    : (p.notes ?? "—")}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex flex-wrap items-center justify-end gap-2">
                                  {!isVoided && p.paidBy == null ? (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      isLoading={reconcileMutation.isPending}
                                      onClick={() => onReconcile(p.id)}
                                    >
                                      ຜູກຜູ້ຮັບເງິນ
                                    </Button>
                                  ) : null}
                                  {!isVoided && canVoid ? (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive hover:text-destructive"
                                      onClick={() => setVoidPayment(p)}
                                    >
                                      ຍົກເລີກ
                                    </Button>
                                  ) : null}
                                  {isVoided ? (
                                    <Badge variant="outline" className="font-normal">
                                      ຍົກເລີກແລ້ວ
                                    </Badge>
                                  ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {invoice.remainingAmount > 0 ? (
                <div className="lg:col-span-4">
                  <div className="lg:sticky lg:top-6">
                    <Card className="gap-0 overflow-hidden border-l-4 border-l-red-600 shadow-sm">
                      <CardHeader className="border-border border-b bg-muted/30 pb-4">
                        <CardTitle className="font-medium text-base">
                          ຮັບຊຳລະເພີ່ມ
                        </CardTitle>
                        <p className="pt-1 text-muted-foreground text-xs leading-snug">
                          ຍອດຄ້າງສູງສຸດ{" "}
                          {invoice.remainingAmount.toLocaleString("lo-LA")} ກີບ
                        </p>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <FormRoot
                          methods={
                            methods as unknown as UseFormReturn<PayFormValues>
                          }
                          onSubmit={onPay}
                        >
                          <div className="flex flex-col gap-4">
                            <FormPrice
                              name="amount"
                              label="ຈຳນວນເງິນ (ກີບ)"
                              currencySymbol="₭"
                              min={1}
                              max={invoice.remainingAmount}
                              step={1}
                              requiredMark
                            />
                            <FormInput name="notes" label="ໝາຍເຫດ (ທາງເລືອກ)" />
                            <Button
                              type="submit"
                              className="w-full bg-red-600 hover:bg-red-700"
                              isLoading={payMutation.isPending}
                              disabled={!id}
                            >
                              ບັນທຶກການຮັບເງິນ
                            </Button>
                          </div>
                        </FormRoot>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex min-h-[30vh] flex-col items-center justify-center gap-2 text-center">
            <FileTextIcon
              className="size-10 stroke-[1.5] text-muted-foreground"
              aria-hidden
            />
            <p className="font-medium text-muted-foreground">ບໍ່ພົບຂໍ້ມູນ</p>
          </div>
        )}
      </Main>

      {id && voidPayment ? (
        <VoidCreditPaymentDialog
          invoiceId={id}
          payment={voidPayment}
          open={voidPayment != null}
          onOpenChange={(open) => {
            if (!open) setVoidPayment(null);
          }}
          onSuccess={() => {
            toast.success("ຍົກເລີກການຮັບເງິນແລ້ວ");
          }}
        />
      ) : null}

      {id ? (
        <VoidCreditInvoiceDialog
          invoiceId={id}
          open={voidInvoiceOpen}
          onOpenChange={setVoidInvoiceOpen}
          onSuccess={() => toast.success("ຍົກເລີກໃບສຳເລັດ")}
        />
      ) : null}
    </>
  );
}
